import type { FC } from "react";
import Link from "next/link";
import { ArrowDownIcon, LockIcon } from "@/components/common/icons";
import { useDisclosure } from "@mantine/hooks";
import { numberWithCommas } from "@/utils/format";
import { Collapse, Button } from "@mantine/core";
import { Airdrop } from "@/types/network";

const AirdropDetail: FC<{
  airdrop: Airdrop;
  withClaimBtn?: boolean;
  onClaim?: () => void;
  claiming?: boolean;
  claimable?: boolean;
  size?: "sm" | "md";
}> = ({ airdrop, withClaimBtn, onClaim, claiming, claimable, size = "md" }) => {
  const [opened, { toggle }] = useDisclosure(false);

  return (
    <div>
      <div
        className={`flex items-center justify-between font-semibold ${
          size === "sm" ? "text-base" : "text-xl"
        } `}
      >
        <div className="flex items-center">
          {airdrop.name}
          {airdrop.locked && (
            <div className="flex items-center ml-2">
              <LockIcon></LockIcon>
              <span className="font-normal text-base text-gray1 ml-1">
                Locked
              </span>
            </div>
          )}
          {airdrop.rule && (
            <a
              className="text-primary action hover:text-primary ml-2 font-medium text-base"
              href={airdrop.rule}
              target="_blank"
              rel="noreferrer"
            >
              view rules
            </a>
          )}
        </div>
        <div className="flex items-center">
          {withClaimBtn && (
            <Button
              variant="outline"
              size="xs"
              radius="xl"
              className="w-[64px] h-[24px]  mr-4"
              onClick={onClaim}
              loading={claiming}
              disabled={!claimable}
            >
              Claim
            </Button>
          )}
          <div
            className="hover:cursor-pointer flex items-center"
            onClick={toggle}
          >
            {airdrop.claimTokens || "--"}{" "}
            <span className="font-normal ml-2 text-base">KAT</span>
            <ArrowDownIcon
              className={`transition-all self-center w-2 ml-2 ${
                airdrop.locked ? "fill-[#E0E1E1]" : ""
              } ${opened ? "rotate-180" : ""}`}
            ></ArrowDownIcon>
          </div>
        </div>
      </div>
      {airdrop.tip && <div className="mt-1 text-sm">{airdrop.tip}</div>}
      <Collapse in={opened}>
        <div className="px-6 mt-4 flex flex-col gap-5">
          <table
            className="w-full"
            style={{
              borderCollapse: "separate",
              borderSpacing: "0 20px",
            }}
          >
            {airdrop.parts.map((part, index) => {
              return (
                <tr key={index}>
                  <td width={"75%"}>
                    <div className="text-sm font-semibold mb-1">
                      {part.label}
                    </div>
                    <div
                      className="text-xs whitespace-pre-line min-h-[20px]"
                      dangerouslySetInnerHTML={{
                        __html: part.desc,
                      }}
                    ></div>
                  </td>
                  <td>
                    <div className="text-sm font-semibold mb-1">Score</div>
                    <div className="text-sm">
                      {part.score === "--" ? "--" : part.score}
                    </div>
                  </td>
                  <td className="text-end">
                    <div className="text-sm font-semibold mb-1">$KAT</div>
                    <div className="text-sm">
                      {part.tokens === "--" ? "--" : part.tokens}
                    </div>
                  </td>
                </tr>
              );
            })}
          </table>
        </div>
      </Collapse>
    </div>
  );
};

export default AirdropDetail;
